import { useState, useEffect, useRef } from "react";

// ─── Brand tokens ────────────────────────────────────────────────────────────
const C = {
  navy:     "#0B1120",
  navyCard: "#0F172A",
  border:   "#1E293B",
  teal:     "#14B8A6",
  tealDark: "#0F766E",
  tealGlow: "rgba(20,184,166,0.18)",
  green:    "#22C55E",
  yellow:   "#EAB308",
  red:      "#EF4444",
  white:    "#F9FAFB",
  muted:    "#94A3B8",
  dim:      "#475569",
};

// ─── Assessment data ──────────────────────────────────────────────────────────
const PILLARS = [
  {
    id: "governance",
    name: "Governance & Communication",
    icon: "📋",
    weight: 20,
    questions: [
      "Does your MSP provide regular monthly or quarterly reports on IT health, security, and risks?",
      "Do they conduct an IT risk or security review with your leadership at least once per year?",
      "Do they document major changes to your environment and make that documentation available to you on request?",
      "Do they follow a formal framework or standard when managing your environment?",
    ],
  },
  {
    id: "security",
    name: "Security Baseline",
    icon: "🔐",
    weight: 25,
    questions: [
      "Is multi-factor authentication enabled for your email and core systems wherever possible?",
      "Do all clinic-owned computers run modern endpoint detection and response software, not just basic antivirus?",
      "Are security patches and updates applied on a regular scheduled basis, at least monthly?",
      "Are user accounts configured with least privilege: no shared logins, limited admin access, role-based permissions?",
    ],
  },
  {
    id: "backup",
    name: "Backup & Continuity",
    icon: "💾",
    weight: 25,
    questions: [
      "Does your MSP manage automated backups of your critical systems: PMS, imaging, and file shares?",
      "Are backups tested by actually restoring data on a regular basis, at least quarterly?",
      "Do you know approximately how long it would take to get your core systems back online after a major outage?",
      "Are backups protected against ransomware with immutable or offsite copies?",
    ],
  },
  {
    id: "network",
    name: "Network & Workstations",
    icon: "🌐",
    weight: 20,
    questions: [
      "Does your MSP manage and monitor your firewall and Wi-Fi including security updates and configuration changes?",
      "Is your network segmented, with guest Wi-Fi separated from clinical systems and administrative systems?",
      "Are shared workstations configured so staff log in as themselves rather than a generic shared account?",
      "Does your MSP maintain an accurate inventory of clinic-owned devices and know which ones are secured and monitored?",
    ],
  },
  {
    id: "accountability",
    name: "Evidence & Accountability",
    icon: "✅",
    weight: 10,
    questions: [
      "If you asked tomorrow, could your MSP provide evidence of recent patching, backup tests, and security alerts?",
      "Do they have documented procedures for handling incidents, outages, or suspected cyberattacks?",
      "Do they regularly audit and remove old or unused accounts from former employees?",
    ],
  },
];

const BLURBS = {
  "Governance & Communication":
    "Clear communication and governance ensure your MSP's work is transparent, documented, and aligned with your practice goals. Regular reporting and risk reviews give you confidence that security is being actively managed rather than handled reactively.",
  "Security Baseline":
    "Strong fundamentals like MFA, modern endpoint protection, patching, and least-privilege access are the foundation of clinic safety. Without them, a single phishing email or weak password can lead to a serious security incident.",
  "Backup & Continuity":
    "Backups that are never tested may fail when you need them most. A mature MSP designs backup and recovery so your practice can return to operation quickly after ransomware, hardware failure, or accidental data loss.",
  "Network & Workstations":
    "Your network and shared workstations are at the heart of daily clinical operations. Proper segmentation, secure Wi-Fi, and individual logins on shared devices greatly reduce the chance that a single compromise disrupts the entire practice.",
  "Evidence & Accountability":
    "The ability to produce proof of patching, backup tests, and incident handling separates a reactive IT vendor from a true partner. Evidence and documentation make your MSP's work measurable, auditable, and trustworthy.",
};

const IDK_FACTOR = 0.4;

const PIMS_OPTIONS = [
  "Cornerstone", "AVImark", "Pulse", "IDEXX Neo", "ezyVet", "ezyVet Enterprise",
  "DVMAX", "Shepherd", "Vetspire", "Other cloud", "Other on-prem", "Not sure",
];

function classifyScore(pct) {
  if (pct >= 90) return { label: "Strong MSP Alignment",        tier: "Your MSP is operating at a high level of maturity.",                                                                          color: C.green,  bg: "rgba(34,197,94,0.12)"  };
  if (pct >= 75) return { label: "Generally Aligned with Gaps", tier: "Your MSP covers most areas but some gaps remain.",                                                                            color: C.teal,   bg: C.tealGlow              };
  if (pct >= 60) return { label: "Needs Improvement",           tier: "Several important safeguards appear to be missing or unclear.",                                                               color: C.yellow, bg: "rgba(234,179,8,0.12)"  };
  return           { label: "High Risk MSP Relationship",       tier: "Your current MSP may be leaving your clinic significantly exposed.",                                                          color: C.red,    bg: "rgba(239,68,68,0.12)"  };
}

// ─── Subcomponents ────────────────────────────────────────────────────────────
function ProgressBar({ step }) {
  const pct = Math.round((step / (PILLARS.length + 1)) * 100);
  const label = step <= PILLARS.length ? `Section ${step} of ${PILLARS.length}` : "Complete";
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: C.muted, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 11, color: C.teal, fontWeight: 700 }}>{pct}%</span>
      </div>
      <div style={{ height: 3, background: C.border, borderRadius: 99, overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: `linear-gradient(90deg, ${C.tealDark}, ${C.teal})`,
          borderRadius: 99,
          transition: "width 0.4s cubic-bezier(0.4,0,0.2,1)",
          boxShadow: `0 0 12px ${C.teal}88`,
        }} />
      </div>
    </div>
  );
}

function ScoreBadge({ score, cls, size = 80 }) {
  const r = (size / 2) - 7;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={6} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={cls.color} strokeWidth={6}
          strokeLinecap="round" strokeDasharray={`${filled} ${circ - filled}`}
          style={{ transition: "stroke-dasharray 0.6s cubic-bezier(0.4,0,0.2,1), stroke 0.3s" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'DM Mono', monospace" }}>
        <span style={{ fontSize: size < 70 ? 14 : 18, fontWeight: 800, color: cls.color, lineHeight: 1 }}>{score}</span>
        <span style={{ fontSize: 9, color: C.muted, lineHeight: 1, marginTop: 2 }}>/ 100</span>
      </div>
    </div>
  );
}

function RadioOption({ name, value, label, checked, onChange }) {
  const colors = {
    yes: { border: C.green,  bg: "rgba(34,197,94,0.1)",  text: "#BBF7D0" },
    idk: { border: C.yellow, bg: "rgba(234,179,8,0.1)",  text: "#FEF9C3" },
    no:  { border: C.red,    bg: "rgba(239,68,68,0.1)",  text: "#FECACA" },
  };
  const active = colors[value];
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
      padding: "10px 16px", borderRadius: 10,
      border: `1px solid ${checked ? active.border : C.border}`,
      background: checked ? active.bg : "transparent",
      color: checked ? active.text : C.muted,
      transition: "all 0.15s ease",
      fontSize: 14, fontWeight: checked ? 600 : 400,
      flex: 1, minWidth: 100,
    }}>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} style={{ display: "none" }} />
      <span style={{
        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
        border: `2px solid ${checked ? active.border : C.dim}`,
        background: checked ? active.border : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s ease",
      }}>
        {checked && <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
      </span>
      {label}
    </label>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function MSPAlignmentCheck() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ practice: "", firstName: "", lastName: "", email: "", phone: "", pims: "", endpointCount: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const topRef = useRef(null);

  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [step]);

  function computeScore() {
    let total = 0;
    PILLARS.forEach(p => {
      const perQ = p.weight / p.questions.length;
      p.questions.forEach((_, qi) => {
        const val = answers[`${p.id}-${qi}`];
        if (val === "yes") total += perQ;
        else if (val === "idk") total += perQ * IDK_FACTOR;
      });
    });
    return Math.min(100, Math.round(total));
  }

  function pillarScore(p) {
    let s = 0;
    const perQ = p.weight / p.questions.length;
    p.questions.forEach((_, qi) => {
      const val = answers[`${p.id}-${qi}`];
      if (val === "yes") s += perQ;
      else if (val === "idk") s += perQ * IDK_FACTOR;
    });
    return Math.round((s / p.weight) * 100);
  }

  function topGaps() {
    return [...PILLARS]
      .map(p => ({ name: p.name, pct: pillarScore(p) }))
      .sort((a, b) => a.pct - b.pct)
      .slice(0, 3)
      .map(x => x.name);
  }

  function pillarAnswered(p) {
    return p.questions.every((_, qi) => answers[`${p.id}-${qi}`]);
  }

  const score = computeScore();
  const cls = classifyScore(score);
  const currentPillar = step >= 1 && step <= PILLARS.length ? PILLARS[step - 1] : null;

  function handleAnswer(pillarId, qi, val) {
    setAnswers(prev => ({ ...prev, [`${pillarId}-${qi}`]: val }));
  }

  function handleNext() { setStep(s => s + 1); }
  function handleBack() { setStep(s => Math.max(0, s - 1)); }

  async function handleSubmit() {
    if (!contact.firstName.trim() || !contact.lastName.trim()) {
      setError("Please enter your first and last name to receive your report.");
      return;
    }
    if (!contact.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      setError("Please enter a valid email address to receive your report.");
      return;
    }
    setError("");
    setSubmitting(true);

    const gaps = topGaps();
    const payload = {
      practice: contact.practice,
      firstName: contact.firstName,
      lastName: contact.lastName,
      contactEmail: contact.email,
      contactPhone: contact.phone,
      pims: contact.pims,
      endpointCount: contact.endpointCount,
      score,
      status: cls.label,
      tier: cls.tier,
      assessmentType: "MSP Alignment",
      recommendations: gaps,
      pillarBreakdown: PILLARS.map(p => ({ name: p.name, score: pillarScore(p) })),
      recommendationSummaryHtml: gaps.map(name => {
        const blurb = BLURBS[name] || "";
        return `<table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:8px;"><tr><td style="background:#F8FAFC;border-radius:8px;padding:12px 14px;border-left:3px solid #EF4444;"><div style="font-size:13px;font-weight:700;color:#0F172A;margin-bottom:4px;">${name}</div><div style="font-size:12px;color:#475569;line-height:1.5;">${blurb}</div></td></tr></table>`;
      }).join(""),
    };

    try {
      const ENDPOINT = "https://a0qpvve7g5.execute-api.us-east-2.amazonaws.com/vetdps-msp-submit";
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.success === false) {
        throw new Error(`Submission failed (status ${res.status})`);
      }
      setSubmitted(true);
    } catch (e) {
      console.error("Submission error:", e);
      setError("We couldn't send your report just now. Please try again in a moment, or email support@heimsys.com and we'll get it to you.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div ref={topRef} style={{
      minHeight: "100vh", background: C.navy,
      fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif",
      color: C.white, padding: "0 16px 80px",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@500;700&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; background: ${C.navy}; }
        input::placeholder { color: #64748B; }
        input:focus, select:focus { outline: none; border-color: ${C.teal} !important; box-shadow: 0 0 0 2px ${C.tealGlow} !important; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.35s ease forwards; }
      `}</style>

      <div style={{ maxWidth: 700, margin: "0 auto", paddingTop: 40 }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            background: C.tealGlow, border: `1px solid ${C.teal}44`,
            borderRadius: 99, padding: "4px 14px", marginBottom: 16,
          }}>
            <span style={{ fontSize: 10, color: C.teal, fontFamily: "'DM Mono', monospace", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
              VetDPS® MSP Alignment Check
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: "clamp(22px, 5vw, 32px)", fontWeight: 800, color: C.white, lineHeight: 1.2 }}>
            Is Your IT Provider<br />
            <span style={{ color: C.teal }}>Actually Protecting You?</span>
          </h1>
          <p style={{ color: C.muted, marginTop: 10, fontSize: 15, lineHeight: 1.6 }}>
            Answer {PILLARS.reduce((s, p) => s + p.questions.length, 0)} questions about your current IT provider.<br />
            Get a scored alignment report delivered to your inbox. Free, no commitment.
          </p>
        </div>

        {/* Live score strip */}
        {step > 0 && !submitted && (
          <div style={{
            display: "flex", alignItems: "center", gap: 16,
            background: C.navyCard, border: `1px solid ${C.border}`,
            borderRadius: 14, padding: "14px 20px", marginBottom: 20,
            animation: "fadeUp 0.3s ease",
          }}>
            <ScoreBadge score={score} cls={cls} size={64} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 3 }}>Current Score</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: cls.color }}>{cls.label}</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2, fontFamily: "'DM Mono', monospace" }}>{cls.tier}</div>
            </div>
            <ProgressBar step={step} />
          </div>
        )}

        {/* STEP 0: Intro */}
        {step === 0 && (
          <div className="fade-up">
            <div style={{ background: C.navyCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, marginBottom: 16 }}>
              <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, margin: 0 }}>
                Most veterinary practices have an IT provider, but very few know whether that provider is
                actually doing the job. This tool evaluates your current MSP against the <strong style={{ color: C.white }}>VetDPS® Framework</strong>,
                the only cybersecurity standard built specifically for veterinary medicine. Answer honestly
                based on what you know, or don't know, about your current setup.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 20 }}>
                {[
                  { icon: "⏱️", label: "~4 minutes" },
                  { icon: "📊", label: "Scored report" },
                  { icon: "📧", label: "Delivered by email" },
                  { icon: "🔒", label: "No commitment" },
                ].map(x => (
                  <div key={x.label} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    background: C.navy, border: `1px solid ${C.border}`,
                    borderRadius: 99, padding: "5px 12px", fontSize: 12, color: C.muted,
                  }}>
                    <span>{x.icon}</span><span>{x.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 24 }}>
              {PILLARS.map(p => (
                <div key={p.id} style={{
                  background: C.navyCard, border: `1px solid ${C.border}`,
                  borderRadius: 12, padding: "12px 14px",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{ fontSize: 20 }}>{p.icon}</span>
                  <span style={{ fontSize: 12, color: C.muted, fontWeight: 500, lineHeight: 1.3 }}>{p.name}</span>
                </div>
              ))}
            </div>
            <button onClick={handleNext} style={btnStyle(C.teal)}>
              Start the Check →
            </button>
          </div>
        )}

        {/* STEP 1–N: Pillar questions */}
        {currentPillar && (
          <div className="fade-up" key={step}>
            <div style={{ background: C.navyCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 28, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
                <span style={{ fontSize: 28 }}>{currentPillar.icon}</span>
                <div>
                  <div style={{ fontSize: 10, color: C.teal, fontFamily: "'DM Mono', monospace", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 3 }}>
                    Section {step} of {PILLARS.length}
                  </div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: C.white }}>{currentPillar.name}</h2>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {currentPillar.questions.map((q, qi) => {
                  const key = `${currentPillar.id}-${qi}`;
                  const val = answers[key];
                  return (
                    <div key={qi}>
                      <div style={{ fontSize: 14, color: C.white, fontWeight: 500, marginBottom: 10, lineHeight: 1.5 }}>
                        <span style={{ color: C.teal, fontFamily: "'DM Mono', monospace", fontWeight: 700, marginRight: 8 }}>
                          {(step - 1) * 4 + qi + 1}.
                        </span>
                        {q}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {[
                          { value: "yes", label: "Yes" },
                          { value: "idk", label: "I don't know" },
                          { value: "no",  label: "No" },
                        ].map(opt => (
                          <RadioOption
                            key={opt.value} name={key} value={opt.value} label={opt.label}
                            checked={val === opt.value}
                            onChange={() => handleAnswer(currentPillar.id, qi, opt.value)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {step > 1 && <button onClick={handleBack} style={btnStyle(C.dim, true)}>← Back</button>}
              <button onClick={handleNext} disabled={!pillarAnswered(currentPillar)}
                style={btnStyle(C.teal, false, !pillarAnswered(currentPillar))}>
                {step === PILLARS.length ? "See My Results →" : "Next →"}
              </button>
            </div>
          </div>
        )}

        {/* Final step: Results + Contact */}
        {step === PILLARS.length + 1 && !submitted && (
          <div className="fade-up">
            {/* Score summary */}
            <div style={{
              background: cls.bg, border: `1px solid ${cls.color}44`,
              borderRadius: 16, padding: 28, marginBottom: 16,
              display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap",
            }}>
              <ScoreBadge score={score} cls={cls} size={90} />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 11, color: C.muted, fontFamily: "'DM Mono', monospace", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>
                  Your MSP Alignment Score
                </div>
                <div style={{ fontSize: 26, fontWeight: 800, color: cls.color, lineHeight: 1.1 }}>{cls.label}</div>
                <div style={{ fontSize: 13, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>{cls.tier}</div>
              </div>
            </div>

            {/* Pillar breakdown */}
            <div style={{ background: C.navyCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>
                Section Breakdown
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {PILLARS.map(p => {
                  const ps = pillarScore(p);
                  const c = ps >= 75 ? C.teal : ps >= 50 ? C.yellow : C.red;
                  return (
                    <div key={p.id}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: C.white }}>{p.icon} {p.name}</span>
                        <span style={{ fontSize: 13, color: c, fontWeight: 700, fontFamily: "'DM Mono', monospace" }}>{ps}%</span>
                      </div>
                      <div style={{ height: 4, background: C.border, borderRadius: 99, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${ps}%`, background: c, borderRadius: 99, transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)", boxShadow: `0 0 8px ${c}66` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top gaps */}
            <div style={{ background: C.navyCard, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Mono', monospace" }}>
                Top Areas to Address
              </h3>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {topGaps().map(name => (
                  <div key={name} style={{ background: C.navy, border: `1px solid ${C.border}`, borderLeft: `3px solid ${C.red}`, borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.white, marginBottom: 4 }}>{name}</div>
                    <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>{BLURBS[name]}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact form */}
            <div style={{ background: C.navyCard, border: `1px solid ${C.teal}44`, borderRadius: 16, padding: 28, marginBottom: 16 }}>
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: C.white }}>
                  Get Your Full MSP Alignment Report
                </h3>
                <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                  Enter your details below. We'll send your personalized report with section scores,
                  gap analysis, and recommended next steps, straight to your inbox.
                </p>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
                {[
                  { key: "practice",  label: "Practice Name",    placeholder: "Lakeside Animal Clinic", type: "text"  },
                  { key: "firstName", label: "First Name *",     placeholder: "Jane",                   type: "text"  },
                  { key: "lastName",  label: "Last Name *",      placeholder: "Smith",                  type: "text"  },
                  { key: "email",     label: "Email Address *",  placeholder: "name@clinic.com",        type: "email" },
                  { key: "phone",     label: "Phone (optional)", placeholder: "(407) 555-5555",         type: "tel"   },
                  { key: "pims",      label: "What practice management software do you use?", placeholder: "Select your system", type: "select", options: PIMS_OPTIONS },
                  { key: "endpointCount", label: "About how many computers does your practice use?", placeholder: "e.g. 12", type: "number" },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: "block", fontSize: 11, color: C.muted, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                      {f.label}
                    </label>
                    {f.type === "select" ? (
                      <select value={contact[f.key]}
                        onChange={e => setContact(c => ({ ...c, [f.key]: e.target.value }))}
                        style={{ width: "100%", padding: "10px 12px", background: C.navy, border: `1px solid ${C.border}`, borderRadius: 8, color: contact[f.key] ? C.white : "#64748B", fontSize: 14, cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s" }}
                      >
                        <option value="">{f.placeholder}</option>
                        {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} placeholder={f.placeholder} value={contact[f.key]}
                        onChange={e => setContact(c => ({ ...c, [f.key]: e.target.value }))}
                        style={{ width: "100%", padding: "10px 12px", background: C.navy, border: `1px solid ${C.border}`, borderRadius: 8, color: C.white, fontSize: 14, transition: "border-color 0.15s, box-shadow 0.15s" }}
                      />
                    )}
                  </div>
                ))}
              </div>
              {error && (
                <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, color: "#FCA5A5", fontSize: 13 }}>
                  {error}
                </div>
              )}
              <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={handleBack} style={btnStyle(C.dim, true)}>← Back</button>
                <button onClick={handleSubmit} disabled={submitting} style={btnStyle(C.teal, false, submitting)}>
                  {submitting ? "Sending..." : "Send My Report →"}
                </button>
              </div>
              <p style={{ margin: "12px 0 0", fontSize: 12, color: C.muted }}>
                Your information is used only to deliver your report and follow up on your results. We don't share or sell contact data.
              </p>
            </div>
          </div>
        )}

        {/* Success screen */}
        {submitted && (
          <div className="fade-up" style={{ background: C.navyCard, border: `1px solid ${C.teal}44`, borderRadius: 16, padding: 40, textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.tealGlow, border: `2px solid ${C.teal}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 20px" }}>✓</div>
            <h2 style={{ margin: "0 0 10px", fontSize: 22, fontWeight: 800, color: C.white }}>Your Report Is On Its Way</h2>
            <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.7, maxWidth: 440, margin: "0 auto 24px" }}>
              We're preparing your personalized MSP Alignment Report and sending it to <strong style={{ color: C.white }}>{contact.email}</strong>.
              Check your inbox within the next few minutes.
            </p>
            <div style={{ background: C.navy, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 20px", maxWidth: 400, margin: "0 auto 24px", textAlign: "left" }}>
              <div style={{ fontSize: 11, color: C.teal, fontFamily: "'DM Mono', monospace", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Your Score</div>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <ScoreBadge score={score} cls={cls} size={60} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: cls.color }}>{cls.label}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4, lineHeight: 1.5 }}>{cls.tier}</div>
                </div>
              </div>
            </div>
            <p style={{ color: C.muted, fontSize: 13, margin: 0 }}>
              A member of the Heimsys team may reach out to walk you through your results.<br />
              Questions? Call us at <a href="tel:4075028601" style={{ color: C.teal, textDecoration: "none" }}>(407) 502-8601</a>
            </p>
          </div>
        )}

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: 32, paddingBottom: 20 }}>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.8 }}>
            <strong style={{ color: C.muted }}>Heimsys LLC</strong> · VetDPS® Managed Security & Compliance<br />
            support@heimsys.com · (407) 502-8601 · heimsys.com
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>© {new Date().getFullYear()} Heimsys LLC · Confidential Proprietary Material</div>
        </div>

      </div>
    </div>
  );
}

function btnStyle(color, ghost = false, disabled = false) {
  return {
    padding: "12px 24px", borderRadius: 999,
    border: ghost ? `1px solid ${C.border}` : "none",
    background: ghost ? "transparent" : disabled ? C.dim : `linear-gradient(135deg, ${color}, ${color}cc)`,
    color: ghost ? C.muted : "#fff",
    fontSize: 14, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.6 : 1,
    boxShadow: (!ghost && !disabled) ? `0 8px 24px ${color}44` : "none",
    transition: "all 0.15s ease",
    fontFamily: "'DM Sans', sans-serif",
    letterSpacing: "0.02em",
  };
}
